import { Reveal } from '@/components/Reveal';
import { StarSky } from '@/components/StarSky';
import { envIssues, isSupabaseConfigured } from '@/config/env';
import { CollectionsScreen } from '@/features/collections/CollectionsScreen';
import { CollectionWishForm } from '@/features/collections/CollectionWishForm';
import { StudioFooter } from '@/features/legal/StudioFooter';

/**
 * Главный экран: диагностика подключения backend + список коллекций.
 *
 * Список — честный первый шаг PHASE 15 (полный визард выбора параметров
 * и загрузки фото сюда не входит). Диагностический блок ниже не дублирует
 * работу AppShell (тот перехватывает состояние unconfigured раньше) — он
 * остаётся ради tests/unit/smoke.test.tsx, который рендерит HomePage в
 * изоляции, без роутера и провайдеров. При подключённом backend блок не
 * показывается: служебное «Состояние сборки» пользователю ни к чему.
 */
export function HomePage() {
  return (
    <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-8 px-6 py-10 lg:max-w-none">
      <header className="relative isolate space-y-3">
        <StarSky />
        <p className="text-sm tracking-[0.12em] text-text-3">АСТРА space | Студия</p>
        <h1 className="font-display text-display text-text">Готовые образы для вас</h1>
        <p className="text-text-2">
          Выберите коллекцию, загрузите фото и отметьте детали карточками — студия создаст портрет в
          стиле коллекции. Писать ничего не нужно.
        </p>
      </header>

      {!isSupabaseConfigured && (
        <section
          className="rounded-card border border-border bg-surface p-5"
          aria-labelledby="status-heading"
        >
          <h2 id="status-heading" className="text-sm font-medium text-text-2">
            Состояние сборки
          </h2>

          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-text-3">Фронтенд</dt>
              <dd className="text-success">запущен</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-text-3">Supabase</dt>
              <dd className={isSupabaseConfigured ? 'text-success' : 'text-danger'}>
                {isSupabaseConfigured ? 'подключён' : 'не настроен'}
              </dd>
            </div>
          </dl>

          {!isSupabaseConfigured && (
            <div className="mt-4 border-t border-border pt-4 text-sm text-text-2">
              <p>
                Скопируйте <code className="text-text">.env.example</code> в{' '}
                <code className="text-text">.env</code> и заполните переменные проекта Supabase.
              </p>
              <ul className="mt-2 list-inside list-disc text-text-3">
                {envIssues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {isSupabaseConfigured && (
        <section aria-labelledby="collections-heading">
          <h2 id="collections-heading" className="font-display text-h2 text-text">
            Коллекции
          </h2>
          <CollectionsScreen />
        </section>
      )}

      {isSupabaseConfigured && (
        <Reveal as="section" aria-labelledby="collection-wish-heading" className="flex flex-col gap-4 lg:max-w-2xl">
          <h2 id="collection-wish-heading" className="font-display text-h2 text-text">
            Какую коллекцию добавить?
          </h2>
          <CollectionWishForm />
        </Reveal>
      )}

      <StudioFooter />
    </main>
  );
}
